import { AnalyticsDataLayer } from '@/types/analytics';

type AnalyticsFieldName = keyof AnalyticsDataLayer;

type PropertiesByEvent = {
  accountView: readonly AnalyticsFieldName[];
  emailSignup: readonly AnalyticsFieldName[];
  errorPresented: readonly AnalyticsFieldName[];
  favoriteTheaterAdd: readonly AnalyticsFieldName[];
  favoriteTheaterRemove: readonly AnalyticsFieldName[];
  foodAndDrink: readonly AnalyticsFieldName[];
  linkClick: readonly AnalyticsFieldName[];
  mdpView: readonly AnalyticsFieldName[];
  movieListView: readonly AnalyticsFieldName[];
  pageView: readonly AnalyticsFieldName[];
  sortFilterSelection: readonly AnalyticsFieldName[];
  theatreView: readonly AnalyticsFieldName[];
  userUpdate: readonly AnalyticsFieldName[];
  userUpdateShippingInfo: readonly AnalyticsFieldName[];
  watchListAdd: readonly AnalyticsFieldName[];
  watchListRemove: readonly AnalyticsFieldName[];
};

const SharedFields = {
  customer: [
    'customerId', 'customerEmail', 'customerType',
  ] as const,
  customerAddress: ['customerCity', 'customerState', 'customerPostalCode', 'customerCountry'] as const,
  customerName: ['customerFirstName', 'customerLastName'] as const,
  link: ['customerId', 'customerType', 'linkAction', 'linkCategory', 'linkName', 'memberType'] as const,
  memberType: ['memberType'] as const,
  page: ['pageName', 'pageType'] as const,
  platform: ['orderPlatform'] as const,
  product: ['productId', 'productName'] as const,
  theater: ['theatreId', 'theatreName', 'auditoriumSize'] as const,
  theaterBase: ['theatreId', 'theatreName'] as const,
} as const;

const AccountViewFields = [
  ...SharedFields.customer,
  ...SharedFields.customerAddress,
  ...SharedFields.customerName,
  ...SharedFields.memberType,
  ...SharedFields.page,
  ...SharedFields.theater,
] as const;

const MDPViewFields = [
  ...SharedFields.customer,
  ...SharedFields.customerAddress,
  ...SharedFields.customerName,
  ...SharedFields.memberType,
  ...SharedFields.page,
  ...SharedFields.theaterBase,
] as const;

const PageViewFields = [
  ...SharedFields.customer,
  ...SharedFields.customerAddress,
  ...SharedFields.customerName,
  ...SharedFields.memberType,
  ...SharedFields.page,
  ...SharedFields.platform,
  ...SharedFields.theater,
] as const;

const TheaterViewFields = [
  ...SharedFields.customer,
  ...SharedFields.customerAddress,
  ...SharedFields.customerName,
  ...SharedFields.memberType,
  ...SharedFields.page,
  ...SharedFields.platform,
  ...SharedFields.theater,
] as const;

const EmailSignupFields = [
  ...SharedFields.customer,
] as const;

const ErrorPresentedFields = [
  ...SharedFields.customer,
  ...SharedFields.page,
  'errorMessage',
] as const;

const FavoriteTheaterFields = [
  ...SharedFields.customer,
  ...SharedFields.link,
] as const;

const WatchListFields = [
  ...SharedFields.customer,
  ...SharedFields.product,
] as const;

const SortFilterSelectionFields = [
  ...SharedFields.customer.filter(field => field !== 'customerEmail'),
  ...SharedFields.link,
  'sortFilterSelection',
] as const;

const UserUpdateFields = [
  ...SharedFields.customer,
  ...SharedFields.customerAddress,
  ...SharedFields.customerName,
] as const;

const UserUpdateShippingInfoFields = UserUpdateFields;

export const propertiesByEvent: PropertiesByEvent = {
  accountView: AccountViewFields,
  emailSignup: EmailSignupFields,
  errorPresented: ErrorPresentedFields,
  favoriteTheaterAdd: FavoriteTheaterFields,
  favoriteTheaterRemove: FavoriteTheaterFields,
  foodAndDrink: PageViewFields,
  linkClick: SharedFields.link,
  mdpView: MDPViewFields,
  movieListView: PageViewFields,
  pageView: PageViewFields,
  sortFilterSelection: SortFilterSelectionFields,
  theatreView: TheaterViewFields,
  userUpdate: UserUpdateFields,
  userUpdateShippingInfo: UserUpdateShippingInfoFields,
  watchListAdd: WatchListFields,
  watchListRemove: WatchListFields,
};
